// Filière group management view.
// Renders one section per level. At the specialisation year(s) — where a base
// filière splits into options — each option is shown as its own collapsible
// sub-section (with its own groups/assignment), plus a "Tronc commun" section
// for any students still on the parent code. Options are NOT separate menu
// entries; they live here under their parent filière.
import React, { useState } from 'react';
import {
  UserPlus,
  Shuffle,
  Plus,
  ChevronDown,
  ChevronRight,
  Loader2,
} from 'lucide-react';
import type {
  GroupeWithCounts,
  GroupImpact,
  StudentCountByNiveau,
  TypeFormation,
} from '@/types/group.types';
import type { FiliereForNavigation } from '@/services/groupsService';
import { getNiveauLabel } from '@/types/group.types';
import { deleteGroupe, assignStudentsRandomly, getGroupImpact } from '@/services/groupsService';
import GroupCard from './GroupCard';
import ViewGroupStudentsModal from './ViewGroupStudentsModal';
import CuteConfirmModal from '@/shared/components/CuteConfirmModal';

export interface OptionFiliereData {
  filiere: FiliereForNavigation;
  groups: GroupeWithCounts[];
  counts: StudentCountByNiveau[];
}

interface Props {
  filiere: FiliereForNavigation;
  academicYearId: string;
  campusId?: string;
  groups: GroupeWithCounts[];
  studentCounts: StudentCountByNiveau[];
  optionsData: OptionFiliereData[];
  isLoading: boolean;
  canManage?: boolean;
  /** False when a campus-restricted user has no campus: writes are hidden. */
  hasCampusAccess?: boolean;
  onCreateGroup: (
    niveau: number,
    typeFormation: TypeFormation,
    targetFiliere: FiliereForNavigation,
    existingGroupCount: number,
  ) => void;
  onAssignStudents: (
    niveau: number,
    typeFormation: TypeFormation,
    targetFiliere: FiliereForNavigation,
    targetGroups: GroupeWithCounts[],
  ) => void;
  onRefresh: () => void;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

const FI = 'INITIALE' as TypeFormation;
const FC = 'CONTINUE' as TypeFormation;

const CLOSED_DELETE_CONFIRM = {
  isOpen: false,
  groupId: '',
  groupName: '',
  studentCount: 0,
  impact: null,
  isLoadingImpact: false,
};

const hasAnyImpact = (impact: GroupImpact | null): boolean =>
  !!impact &&
  impact.students + impact.grades + impact.attendance + impact.scheduleEntries + impact.rattrapages + impact.finalizations > 0;

const FiliereGroupsView: React.FC<Props> = ({
  filiere,
  academicYearId,
  campusId,
  groups,
  studentCounts,
  optionsData,
  isLoading,
  canManage = true,
  hasCampusAccess = true,
  onCreateGroup,
  onAssignStudents,
  onRefresh,
  onSuccess,
  onError,
}) => {
  const [expandedNiveaux, setExpandedNiveaux] = useState<Set<number>>(new Set([1, 2, 3, 4, 5]));
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  const [isRandomAssigning, setIsRandomAssigning] = useState<{
    niveau: number;
    type: TypeFormation;
    filiereId: string;
  } | null>(null);
  const [viewGroupId, setViewGroupId] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    isOpen: boolean;
    groupId: string;
    groupName: string;
    studentCount: number;
    impact: GroupImpact | null;
    isLoadingImpact: boolean;
  }>(CLOSED_DELETE_CONFIRM);
  const [isDeleting, setIsDeleting] = useState(false);
  const canWrite = canManage && hasCampusAccess;

  const toggleNiveau = (niveau: number): void => {
    setExpandedNiveaux((prev) => {
      const next = new Set(prev);
      if (next.has(niveau)) next.delete(niveau);
      else next.add(niveau);
      return next;
    });
  };

  const toggleSection = (key: string): void => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const countsForNiveau = (
    counts: StudentCountByNiveau[],
    niveau: number,
  ): StudentCountByNiveau | null => counts.find((c) => c.niveau === niveau) || null;

  // The year at which this filière splits into options.
  // Prefer the explicit specialisationStartSemester; otherwise fall back to the
  // earliest level where an option already has students or groups.
  const branchYear = (() => {
    if (filiere.specialisationStartSemester) {
      return Math.ceil(filiere.specialisationStartSemester / 2);
    }
    let min = filiere.nombreAnnees;
    for (const od of optionsData) {
      for (const c of od.counts) if (c.fiCount + c.fcCount > 0) min = Math.min(min, c.niveau);
      for (const g of od.groups) min = Math.min(min, g.niveau);
    }
    return min;
  })();

  const optionsAtNiveau = (niveau: number): OptionFiliereData[] =>
    optionsData.filter((od) => niveau >= branchYear && niveau <= od.filiere.nombreAnnees);

  // Header summary = parent + options at that level (so the collapsed row totals right)
  const headerSummary = (
    niveau: number,
    optionsHere: OptionFiliereData[],
  ): { fiCount: number; fcCount: number; fiPending: number; fcPending: number } => {
    const acc = { fiCount: 0, fcCount: 0, fiPending: 0, fcPending: 0 };
    const add = (c: StudentCountByNiveau | null): void => {
      if (!c) return;
      acc.fiCount += c.fiCount;
      acc.fcCount += c.fcCount;
      acc.fiPending += c.fiPending;
      acc.fcPending += c.fcPending;
    };
    add(countsForNiveau(studentCounts, niveau));
    for (const od of optionsHere) add(countsForNiveau(od.counts, niveau));
    return acc;
  };

  const handleRandomAssign = async (
    niveau: number,
    type: TypeFormation,
    targetFiliere: FiliereForNavigation,
  ): Promise<void> => {
    setIsRandomAssigning({ niveau, type, filiereId: targetFiliere.id });
    try {
      const result = await assignStudentsRandomly(
        targetFiliere.id,
        niveau,
        type,
        academicYearId,
        campusId,
      );
      onSuccess(
        result.assignedCount > 0
          ? `${result.assignedCount} étudiant(s) assigné(s) aléatoirement`
          : 'Aucun étudiant en attente à assigner',
      );
      onRefresh();
    } catch (err) {
      onError(err instanceof Error ? err.message : "Erreur lors de l'assignation");
    } finally {
      setIsRandomAssigning(null);
    }
  };

  // Open the confirmation right away, then fill in what depends on the group.
  const handleDeleteGroup = async (groupId: string, groupName: string, studentCount: number): Promise<void> => {
    setDeleteConfirm({ isOpen: true, groupId, groupName, studentCount, impact: null, isLoadingImpact: true });
    try {
      const impact = await getGroupImpact(groupId);
      setDeleteConfirm((prev) =>
        prev.groupId === groupId ? { ...prev, impact, isLoadingImpact: false } : prev,
      );
    } catch (err) {
      setDeleteConfirm((prev) => (prev.groupId === groupId ? { ...prev, isLoadingImpact: false } : prev));
      onError(err instanceof Error ? err.message : "Impossible de charger l'impact de la suppression");
    }
  };

  const confirmDeleteGroup = async (): Promise<void> => {
    setIsDeleting(true);
    try {
      await deleteGroupe(deleteConfirm.groupId);
      onSuccess(`Groupe "${deleteConfirm.groupName}" masqué — l'historique est conservé`);
      onRefresh();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Erreur lors de la suppression');
    } finally {
      setIsDeleting(false);
      setDeleteConfirm(CLOSED_DELETE_CONFIRM);
    }
  };

  const renderImpactDetails = (): React.ReactNode => {
    const { impact, isLoadingImpact } = deleteConfirm;
    if (isLoadingImpact) {
      return (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Analyse de l'impact…
        </div>
      );
    }
    if (!hasAnyImpact(impact) || !impact) return null;
    const rows: { label: string; value: number }[] = [
      { label: 'étudiant(s)', value: impact.students },
      { label: 'note(s)', value: impact.grades },
      { label: 'présence(s)', value: impact.attendance },
      { label: 'séance(s) planifiée(s)', value: impact.scheduleEntries },
      { label: 'rattrapage(s)', value: impact.rattrapages },
      { label: 'finalisation(s) de notes', value: impact.finalizations },
    ];
    return (
      <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900 space-y-2">
        <p className="font-medium">Ce groupe contient :</p>
        <ul className="grid grid-cols-2 gap-x-4 gap-y-1">
          {rows
            .filter((r) => r.value > 0)
            .map((r) => (
              <li key={r.label}>
                <span className="font-semibold">{r.value}</span> {r.label}
              </li>
            ))}
        </ul>
        <p className="text-xs text-amber-800">
          Rien ne sera supprimé : l'historique est conservé et le groupe sera masqué. Les étudiants
          repasseront en attente d'affectation.
        </p>
      </div>
    );
  };

  // Render one FI or FC section for a given filière context (parent or option)
  const renderTypeSection = (
    type: TypeFormation,
    targetFiliere: FiliereForNavigation,
    niveau: number,
    counts: StudentCountByNiveau | null,
    typeGroups: GroupeWithCounts[],
  ): React.ReactNode => {
    const label = type === FI ? 'Formation Initiale (FI)' : 'Formation Continue (FC)';
    const shortLabel = type === FI ? 'FI' : 'FC';
    const pending = counts ? (type === FI ? counts.fiPending : counts.fcPending) : 0;
    const busy =
      isRandomAssigning?.niveau === niveau &&
      isRandomAssigning?.type === type &&
      isRandomAssigning?.filiereId === targetFiliere.id;

    return (
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-medium text-gray-700">{label}</h4>
          {canWrite && (
            <div className="flex items-center gap-2">
              {pending > 0 && typeGroups.length > 0 && (
                <button
                  onClick={() => handleRandomAssign(niveau, type, targetFiliere)}
                  disabled={isRandomAssigning !== null}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 rounded-lg hover:bg-amber-100 disabled:opacity-50"
                >
                  {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Shuffle className="h-3 w-3" />}
                  Assignation aléatoire
                </button>
              )}
              {pending > 0 && typeGroups.length > 0 && (
                <button
                  onClick={() => onAssignStudents(niveau, type, targetFiliere, typeGroups)}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-primary-700 bg-primary-50 rounded-lg hover:bg-primary-100"
                >
                  <UserPlus className="h-3 w-3" />
                  Assigner manuellement
                </button>
              )}
              <button
                onClick={() => onCreateGroup(niveau, type, targetFiliere, typeGroups.length)}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                <Plus className="h-3 w-3" />
                Créer groupe(s)
              </button>
            </div>
          )}
        </div>

        {typeGroups.length === 0 ? (
          <div className="p-4 bg-gray-50 rounded-lg text-center text-sm text-gray-500">
            Aucun groupe {shortLabel} pour ce niveau
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-3">
            {typeGroups.map((group) => (
              <GroupCard
                key={group.id}
                group={group}
                canManage={canWrite}
                onView={() => setViewGroupId(group.id)}
                onDelete={() => handleDeleteGroup(group.id, group.name, group.studentCount)}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  // Render the FI + FC pair for a filière context at a level
  const renderFormationBlock = (
    targetFiliere: FiliereForNavigation,
    niveau: number,
    counts: StudentCountByNiveau | null,
    filiereGroups: GroupeWithCounts[],
  ): React.ReactNode => {
    const fiGroups = filiereGroups.filter((g) => g.niveau === niveau && g.typeFormation === FI);
    const fcGroups = filiereGroups.filter((g) => g.niveau === niveau && g.typeFormation === FC);
    return (
      <div className="space-y-6">
        {renderTypeSection(FI, targetFiliere, niveau, counts, fiGroups)}
        {renderTypeSection(FC, targetFiliere, niveau, counts, fcGroups)}
      </div>
    );
  };

  // Render a collapsible sub-section (used for Tronc commun and each option)
  const renderCollapsible = (
    key: string,
    title: string,
    counts: StudentCountByNiveau | null,
    children: React.ReactNode,
  ): React.ReactNode => {
    const collapsed = collapsedSections.has(key);
    return (
      <div key={key} className="rounded-lg border border-gray-200 overflow-hidden">
        <button
          onClick={() => toggleSection(key)}
          className="w-full px-3 py-2 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center gap-2">
            {collapsed ? (
              <ChevronRight className="h-4 w-4 text-gray-400" />
            ) : (
              <ChevronDown className="h-4 w-4 text-gray-400" />
            )}
            <span className="text-sm font-semibold text-gray-800">{title}</span>
          </div>
          {counts && (
            <span className="text-xs text-gray-500">
              FI: {counts.fiCount} <span className="text-amber-600">({counts.fiPending} en attente)</span>
              {counts.fcCount > 0 && <span className="ml-2">FC: {counts.fcCount}</span>}
            </span>
          )}
        </button>
        {!collapsed && <div className="p-3">{children}</div>}
      </div>
    );
  };

  // All groups across parent + options (for the view-students modal lookup)
  const allGroups: GroupeWithCounts[] = [groups, ...optionsData.map((o) => o.groups)].flat();

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-12 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Niveaux */}
      <div className="space-y-4">
        {Array.from({ length: filiere.nombreAnnees }, (_, i) => i + 1).map((niveau) => {
          const isExpanded = expandedNiveaux.has(niveau);
          const optionsHere = optionsAtNiveau(niveau);
          const parentCounts = countsForNiveau(studentCounts, niveau);
          const parentHasStudents = !!parentCounts && parentCounts.fiCount + parentCounts.fcCount > 0;
          const parentHasGroups = groups.some((g) => g.niveau === niveau);
          const summary = headerSummary(niveau, optionsHere);

          return (
            <div key={niveau} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              {/* Niveau header */}
              <button
                onClick={() => toggleNiveau(niveau)}
                className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  {isExpanded ? (
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  )}
                  <span className="font-medium text-gray-900">{getNiveauLabel(niveau)}</span>
                  {optionsHere.length > 0 && (
                    <span className="text-xs text-primary-600 bg-primary-50 px-2 py-0.5 rounded-full">
                      {optionsHere.length} option{optionsHere.length > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-gray-600">
                    FI: {summary.fiCount}{' '}
                    <span className="text-amber-600">({summary.fiPending} en attente)</span>
                  </span>
                  <span className="text-gray-600">
                    FC: {summary.fcCount}{' '}
                    <span className="text-amber-600">({summary.fcPending} en attente)</span>
                  </span>
                </div>
              </button>

              {/* Niveau content */}
              {isExpanded && (
                <div className="p-4 space-y-4">
                  {optionsHere.length === 0 ? (
                    // No options at this level → single parent section
                    renderFormationBlock(filiere, niveau, parentCounts, groups)
                  ) : (
                    <>
                      {/* Tronc commun — only when parent still has students/groups here */}
                      {(parentHasStudents || parentHasGroups) &&
                        renderCollapsible(
                          `${niveau}:tronc`,
                          'Tronc commun (non affectés)',
                          parentCounts,
                          renderFormationBlock(filiere, niveau, parentCounts, groups),
                        )}

                      {/* One collapsible per option */}
                      {optionsHere.map((od) =>
                        renderCollapsible(
                          `${niveau}:${od.filiere.id}`,
                          od.filiere.optionName || od.filiere.filiere,
                          countsForNiveau(od.counts, niveau),
                          renderFormationBlock(
                            od.filiere,
                            niveau,
                            countsForNiveau(od.counts, niveau),
                            od.groups,
                          ),
                        ),
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* View Group Students Modal */}
      {viewGroupId &&
        (() => {
          const viewGroup = allGroups.find((g) => g.id === viewGroupId);
          if (!viewGroup) return null;
          // Reassignment stays within the same filière + level + type
          const sameGroups = allGroups.filter(
            (g) =>
              g.filiereId === viewGroup.filiereId &&
              g.niveau === viewGroup.niveau &&
              g.typeFormation === viewGroup.typeFormation,
          );
          return (
            <ViewGroupStudentsModal
              group={viewGroup}
              allGroups={sameGroups}
              canManage={canWrite}
              onClose={() => setViewGroupId(null)}
              onSuccess={() => {
                onSuccess('Opération réussie');
                onRefresh();
              }}
              onError={onError}
            />
          );
        })()}

      {/* Delete Confirmation — soft delete, history kept */}
      <CuteConfirmModal
        isOpen={deleteConfirm.isOpen}
        title="Supprimer le groupe"
        itemName={deleteConfirm.groupName}
        message={
          hasAnyImpact(deleteConfirm.impact)
            ? 'Vérifiez ce qui dépend de ce groupe avant de confirmer.'
            : deleteConfirm.studentCount > 0
              ? `Les ${deleteConfirm.studentCount} étudiant(s) assigné(s) seront remis en attente d'affectation. Le groupe sera masqué, rien n'est effacé.`
              : "Ce groupe est vide. Il sera masqué, rien n'est effacé."
        }
        details={renderImpactDetails()}
        variant="warning"
        size="md"
        confirmLabel="Supprimer"
        cancelLabel="Annuler"
        isLoading={isDeleting || deleteConfirm.isLoadingImpact}
        onConfirm={confirmDeleteGroup}
        onCancel={() => setDeleteConfirm(CLOSED_DELETE_CONFIRM)}
      />
    </div>
  );
};

export default FiliereGroupsView;
